// src/components/Footer.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { WHATSAPP_BOOKING_URL } from '../constants/contact';

// Styled Components
const FooterContainer = styled.footer`
    background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
    color: white;
    padding: 4rem 0 2rem;
    position: relative;
`;

const FooterContent = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
`;

const FooterGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 3rem;
    margin-bottom: 3rem;

    @media (max-width: 768px) {
        gap: 2rem;
    }
`;

const FooterSection = styled(motion.div)`
    h3 {
        color: #fbbf24;
        font-size: 1.25rem;
        font-weight: 700;
        margin-bottom: 1.5rem;
        position: relative;

        &::after {
            content: '';
            position: absolute;
            bottom: -0.5rem;
            left: 0;
            width: 40px;
            height: 2px;
            background: #fbbf24;
        }
    }
`;

const CompanyInfo = styled.div`
    p {
        color: #d1d5db;
        line-height: 1.6;
        margin-bottom: 1rem;

        strong {
            color: white;
            font-weight: 600;
        }
    }
`;

const ContactList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;

    li {
        color: #d1d5db;
        margin-bottom: 1rem;
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        line-height: 1.5;

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const ContactIcon = styled.span`
    color: #fbbf24;
    font-size: 1.1rem;
    min-width: 20px;
`;

const QuickLinks = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const FooterLink = styled.a`
    color: #d1d5db;
    text-decoration: none;
    transition: color 0.2s ease;
    cursor: pointer;

    &:hover {
        color: #fbbf24;
    }
`;

const FeaturedSection = styled.div`
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%);
    padding: 1.5rem;
    border-radius: 12px;
    border: 1px solid rgba(255, 215, 0, 0.3);
    text-align: center;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        border-color: rgba(255, 215, 0, 0.5);
        box-shadow: 0 8px 25px rgba(255, 215, 0, 0.1);
    }
`;

const FeaturedImage = styled.img`
    max-width: 200px;
    height: auto;
    margin-bottom: 1rem;
`;

const FeaturedText = styled.p`
    color: #d1d5db;
    font-size: 0.9rem;
    line-height: 1.5;
    margin: 0;

    strong {
        color: #fbbf24;
        font-weight: 600;
    }
`;

const PaymentSection = styled.div`
    background: rgba(255, 255, 255, 0.05);
    padding: 1.5rem;
    border-radius: 12px;
    border-left: 4px solid #10b981;
    text-align: center;
`;

const PaymentImage = styled.img`
    max-width: 100%;
    height: auto;
    margin-bottom: 1rem;
    border-radius: 8px;
`;

const PaymentText = styled.div`
    color: #d1d5db;
    font-size: 0.9rem;
    line-height: 1.5;

    strong {
        color: #10b981;
        font-weight: 600;
    }

    p {
        margin: 0.5rem 0;
    }
`;

const FooterBottom = styled.div`
    border-top: 1px solid #4b5563;
    padding-top: 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;

    @media (max-width: 768px) {
        flex-direction: column;
        text-align: center;
    }
`;

const Copyright = styled.p`
    color: #9ca3af;
    font-size: 0.9rem;
    margin: 0;
`;

const LegalLinks = styled.div`
    display: flex;
    gap: 1.5rem;

    @media (max-width: 768px) {
        justify-content: center;
    }
`;

const LegalLink = styled.a`
    color: #9ca3af;
    text-decoration: none;
    font-size: 0.9rem;
    transition: color 0.2s ease;

    &:hover {
        color: #fbbf24;
    }
`;

const SocialLinks = styled.div`
    display: flex;
    gap: 1rem;

    @media (max-width: 768px) {
        justify-content: center;
    }
`;

const SocialLink = styled(motion.a)`
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
        background: #fbbf24;
        transform: translateY(-2px);
    }
`;

const MapLink = styled(motion.a)`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #fbbf24;
    text-decoration: none;
    font-weight: 500;
    margin-top: 1rem;
    padding: 0.5rem 1rem;
    background: rgba(251, 191, 36, 0.1);
    border-radius: 6px;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(251, 191, 36, 0.2);
        transform: translateX(5px);
    }
`;

const BadgeContainer = styled.div`
    display: flex;
    gap: 1rem;
    margin-top: 1.5rem;
    flex-wrap: wrap;
`;

const TrustBadge = styled.div`
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    border: 1px solid rgba(16, 185, 129, 0.3);
`;

// Main Footer Component
interface FooterProps {
    onNavigate: (page: string) => void;
}

const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
    return (
        <FooterContainer>
            <FooterContent>
                <FooterGrid>
                    {/* Company Information */}
                    <FooterSection
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <h3>DRL Business</h3>
                        <CompanyInfo>
                            <p>
                                Your trusted partner for comfortable and safe passenger transport
                                throughout Central Europe. Specializing in tailor-made tours and
                                airport transfers.
                            </p>
                            <p>
                                <strong>Business ID:</strong> 56927436<br />
                                <strong>VAT ID:</strong> SK2122508443<br />
                                <strong>Registered:</strong> Commercial Register Bratislava
                            </p>
                        </CompanyInfo>

                        <BadgeContainer>
                            <TrustBadge>✓ Licensed Operator</TrustBadge>
                            <TrustBadge>✓ Insured</TrustBadge>
                            <TrustBadge>✓ 5★ Rated</TrustBadge>
                        </BadgeContainer>
                    </FooterSection>

                    {/* Contact Information */}
                    <FooterSection
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        viewport={{ once: true }}
                    >
                        <h3>Contact Us</h3>
                        <ContactList>
                            <li>
                                <ContactIcon>📞</ContactIcon>
                                <div>
                                    <strong>Phone & WhatsApp</strong><br />
                                    +421 951 074 538
                                </div>
                            </li>
                            <li>
                                <ContactIcon>✉️</ContactIcon>
                                <div>
                                    <strong>Email</strong><br />
                                    drlbusiness.sro@gmail.com
                                </div>
                            </li>
                            <li>
                                <ContactIcon>🏢</ContactIcon>
                                <div>
                                    <strong>Address</strong><br />
                                    Budatinska 3230/16A<br />
                                    85106 Bratislava, Slovakia, EU
                                </div>
                            </li>
                        </ContactList>

                        <MapLink
                            href="https://maps.google.com/?q=Budatinska 3230/16A, 85106 Bratislava, Slovakia"
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            📍 View on Map
                        </MapLink>
                    </FooterSection>

                    {/* Featured on GetYourGuide */}
                    <FooterSection
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        <h3>Featured On</h3>
                        <FeaturedSection>
                            <FeaturedImage
                                src="/img_7.png"
                                alt="GetYourGuide"
                            />
                            <FeaturedText>
                                <strong>Official Partner</strong><br />
                                You can find us also on our partners website GetYourGuide.com
                            </FeaturedText>
                        </FeaturedSection>

                        <SocialLinks style={{ marginTop: '1.5rem' }}>
                            <SocialLink
                                href={WHATSAPP_BOOKING_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                title="Contact us on WhatsApp"
                            >
                                💬
                            </SocialLink>
                            <SocialLink
                                href="mailto:drlbusiness.sro@gmail.com"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                title="Send us an email"
                            >
                                ✉️
                            </SocialLink>
                            <SocialLink
                                href="tel:+421951074538"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                title="Call us"
                            >
                                📞
                            </SocialLink>
                        </SocialLinks>
                    </FooterSection>

                    {/* Secure Checkout */}
                    <FooterSection
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        viewport={{ once: true }}
                    >
                        <h3>Secure Checkout</h3>
                        <PaymentSection>
                            <PaymentImage
                                src="/img.png"
                                alt="Secure Payment Methods"
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                }}
                            />
                            <PaymentText>
                                <p>
                                    <strong>100% Secure Payments</strong><br />
                                    Your payment information is protected with bank-level security and encryption.
                                </p>
                                <p>
                                    <strong>Multiple Payment Options</strong><br />
                                    Credit Cards • Debit Cards • Bank Transfer • PayPal
                                </p>
                                <p>
                                    <strong>SSL Encrypted</strong><br />
                                    All transactions are secured with 256-bit SSL encryption.
                                </p>
                            </PaymentText>
                        </PaymentSection>
                    </FooterSection>
                </FooterGrid>

                {/* Footer Bottom */}
                <FooterBottom>
                    <Copyright>
                        © {new Date().getFullYear()} DRL business s.r.o. All rights reserved.
                    </Copyright>

                    <LegalLinks>
                        <LegalLink href="#privacy">Privacy Policy</LegalLink>
                        <LegalLink href="#terms">Terms of Service</LegalLink>
                        <LegalLink href="#cookies">Cookies</LegalLink>
                    </LegalLinks>

                    <Copyright>
                        Registered in Commercial Register, Section B, Insert 187453
                    </Copyright>
                </FooterBottom>
            </FooterContent>
        </FooterContainer>
    );
};

export default Footer;
